import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useGetFeatureFlagsQuery } from "@/store/featureFlagAPI"

const FF_PREFIX = "ff_"
const DEV_FF_STORAGE_KEY = "moogli:dev-feature-flag-overrides:v1"

type FeatureFlagOverrides = Record<string, boolean>

function readStoredOverrides(): FeatureFlagOverrides {
  if (!import.meta.env.DEV || typeof window === "undefined") return {}

  try {
    const rawOverrides = window.localStorage.getItem(DEV_FF_STORAGE_KEY)
    if (!rawOverrides) return {}

    const parsedOverrides = JSON.parse(rawOverrides)
    if (!parsedOverrides || typeof parsedOverrides !== "object" || Array.isArray(parsedOverrides)) return {}

    return Object.fromEntries(
      Object.entries(parsedOverrides).filter(
        (entry): entry is [string, boolean] => typeof entry[0] === "string" && typeof entry[1] === "boolean",
      ),
    )
  } catch {
    return {}
  }
}

function writeStoredOverrides(overrides: FeatureFlagOverrides) {
  if (!import.meta.env.DEV || typeof window === "undefined") return

  try {
    if (Object.keys(overrides).length === 0) {
      window.localStorage.removeItem(DEV_FF_STORAGE_KEY)
      return
    }
    window.localStorage.setItem(DEV_FF_STORAGE_KEY, JSON.stringify(overrides))
  } catch {
    // localStorage can be disabled or full; URL overrides should still work.
  }
}

function parseUrlOverrides(searchParams: URLSearchParams): FeatureFlagOverrides {
  const overrides: FeatureFlagOverrides = {}
  for (const [param, value] of searchParams.entries()) {
    if (!param.startsWith(FF_PREFIX)) continue
    const flagKey = param.slice(FF_PREFIX.length)
    if (flagKey) {
      overrides[flagKey] = value === "true" || value === "1"
    }
  }
  return overrides
}

function useDevOverrides(): FeatureFlagOverrides {
  const [searchParams] = useSearchParams()
  const [storedOverrides, setStoredOverrides] = useState<FeatureFlagOverrides>(() => readStoredOverrides())

  const urlOverrides = useMemo(() => {
    if (!import.meta.env.DEV) return {}
    return parseUrlOverrides(searchParams)
  }, [searchParams])

  useEffect(() => {
    if (!import.meta.env.DEV || Object.keys(urlOverrides).length === 0) return

    setStoredOverrides((currentOverrides) => {
      const nextOverrides = { ...currentOverrides, ...urlOverrides }
      writeStoredOverrides(nextOverrides)
      return nextOverrides
    })
  }, [urlOverrides])

  return useMemo(() => ({ ...storedOverrides, ...urlOverrides }), [storedOverrides, urlOverrides])
}

export function useFeatureFlag(key: string): boolean {
  const { data } = useGetFeatureFlagsQuery()
  const overrides = useDevOverrides()

  if (key in overrides) return overrides[key]
  return data?.flags?.[key] ?? false
}

/**
 * Whether flag `key` is known yet. `useFeatureFlag` reads a flag that has not
 * loaded (or failed to load) as off, so a page gated on a flag must wait for
 * `ready` before deciding, and offer `retry` when the request `failed`
 * (MPE-1367): otherwise the first load showed "Coming Soon" for a feature that
 * is on, and a failed request left it there until a reload.
 *
 * An explicit dev override (`?ff_<key>=` or stored) decides the flag before the
 * API does, as in `useFeatureFlag`, so it is ready whatever the request does.
 */
export function useFeatureFlagsStatus(key?: string) {
  const { data, isError, isFetching, refetch } = useGetFeatureFlagsQuery()
  const overrides = useDevOverrides()
  const known = data !== undefined || (key !== undefined && key in overrides)
  return {
    ready: known,
    failed: !known && isError && !isFetching,
    retry: refetch,
  }
}

export function useFeatureFlags(): FeatureFlagOverrides {
  const { data } = useGetFeatureFlagsQuery()
  const overrides = useDevOverrides()
  return { ...(data?.flags ?? {}), ...overrides }
}
